import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { CurrencyProvider, useCurrency, useUuid } from '@procureai/ui-mui';
import Decimal from 'decimal.js';
import { isNotNil } from 'ramda';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatActionNext } from '~/components/ChatStep/ChatActions/ChatActionNext';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { useChatSession } from '~/modules';

import { BudgetAlert } from './components/BudgetAlert';
import { IntakeBudgetForm } from './components/IntakeBudgetForm';
import { INTAKE_BUDGET_SET_MUTATION } from './IntakeBudgetStep.mutation.gql';
import { INTAKE_BUDGET } from './IntakeBudgetStep.query.gql';
import { IntakeBudgetStepFormData, intakeBudgetStepSchema } from './IntakeBudgetStep.schema';

const toDecimalOrNull = (value?: string | number | null): Decimal | null => (value ? new Decimal(value) : null);

export const IntakeBudgetStep = ({ role, createdBy, chatSessionCompleted }: ChatMessageProps) => {
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeBudgetStep' });
  const session = useChatSession();

  const formId = useUuid();
  const form = useForm<IntakeBudgetStepFormData>({
    mode: 'onTouched',
    resolver: zodResolver(intakeBudgetStepSchema),
  });

  const { data, loading } = useQuery(INTAKE_BUDGET, {
    variables: session,
  });

  useEffect(() => {
    const budget = data?.sevenbridges.chatSession.request?.budget;
    if (budget) {
      form.reset({ budget });
    }
  }, [form, data]);

  const [updateBudget, { loading: loadingSave }] = useMutation(INTAKE_BUDGET_SET_MUTATION, {
    refetchQueries: [{ query: INTAKE_BUDGET, variables: session }],
  });
  const { format, code } = useCurrency({ code: data?.sevenbridges.chatSession.request?.currency ?? DEFAULT_CURRENCY });

  const quotedValue = toDecimalOrNull(data?.sevenbridges.chatSession.quote?.quotedValue) ?? new Decimal(0);
  const budget = toDecimalOrNull(form.watch('budget'));

  const isLoading = loading || loadingSave;
  return (
    <ChatStep
      title={tIntake('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message', { quotedValue: format(quotedValue.toNumber()) })}
      footer={<ChatActionNext formId={formId} disabled={chatSessionCompleted} />}
    >
      <CurrencyProvider value={code}>
        <Box>
          <FormProvider {...form}>
            <form
              id={formId}
              onSubmit={form.handleSubmit((data) => {
                updateBudget({
                  variables: {
                    data: { budget: isNotNil(data.budget) ? data.budget.toString() : null, chatSessionId: session.id },
                  },
                });
              })}
              autoComplete="off"
              noValidate
            >
              <IntakeBudgetForm disabled={isLoading || chatSessionCompleted} />
            </form>
          </FormProvider>
        </Box>
        <Box
          sx={{
            paddingTop: 3,
          }}
        >
          <BudgetAlert quotedValue={quotedValue} budget={budget} />
        </Box>
      </CurrencyProvider>
    </ChatStep>
  );
};
